import Dexie from "dexie";

const DB_NAME = "relato-ocorrencia";
const TABELA = "anexosPendentes";
const TABELA_ORIGEM = "anexosTipoOrigem";
const TABELA_POR_TIPO = "anexosPorTipo";
const TTL_MS = 12 * 60 * 60 * 1000; // 12h (igual ao rascunho da sessão)

let db = null;

function getDb() {
  if (db) return db;
  try {
    db = new Dexie(DB_NAME);
    db.version(1).stores({ [TABELA]: "id, draft" });
    db.version(2).stores({ [TABELA]: "id, draft, localId" });
    db.version(3).stores({
      [TABELA]: "id, draft, localId",
      [TABELA_ORIGEM]: "id, draft, sqUpload"
    });
    db.version(4).stores({
      [TABELA]: "id, draft, localId",
      [TABELA_ORIGEM]: "id, draft, sqUpload",
      [TABELA_POR_TIPO]: "id, draft, tipoOcorrencia"
    });
    return db;
  } catch (_e) {
    return null;
  }
}

export async function limparTodosAnexos() {
  const banco = getDb();
  if (!banco) return;
  await banco.transaction("rw", banco.tables, async () => {
    await Promise.all(banco.tables.map(tabela => tabela.clear()));
  });
}

function temArquivo(a) {
  return !!(a && a.arquivo instanceof Blob);
}

function aindaPendente(a) {
  return temArquivo(a);
}

function chaveRegistro(draft, id) {
  return `${String(draft)}:${String(id)}`;
}

export async function salvarAnexosPendentes(draft, lista) {
  const banco = getDb();
  if (!banco || !draft) return;
  try {
    const tabela = banco.table(TABELA);
    const pendentes = (lista || []).filter(aindaPendente);
    const registros = pendentes.map(a => ({
      id: chaveRegistro(draft, a.id),
      localId: String(a.id),
      draft,
      nome: a.nome || "",
      tipoArquivo: a.tipoArquivo || "",
      tamanho: a.tamanho || 0,
      slot: a.slot ?? null,
      arquivo: a.arquivo,
      tipoAnexo: a.tipoAnexo ?? null,
      tipoOcorrenciaOrigem: a.tipoOcorrenciaOrigem ?? null,
      sqTipoAnexo: a.sqTipoAnexo ?? null,
      sqUpload: a.sqUpload ?? null,
      dsTipoAnexoFull: a.dsTipoAnexoFull ?? null,
      rotuloAnexo: a.rotuloAnexo ?? null,
      obrigatorioAnexo: a.obrigatorioAnexo ?? null,
      dataInclusao: a.dataInclusao ?? null,
      usuarioInclusao: a.usuarioInclusao ?? null,
      substituindoSqUpload: a.substituindoSqUpload ?? null,
      substituicaoExclusaoConfirmada: a.substituicaoExclusaoConfirmada === true,
      pendenteSubstituicao: !!a.pendenteSubstituicao,
      anexoAnterior: a.anexoAnterior ?? null,
      persistido: !!a.persistido,
      status: a.statusUpload ?? null,
      erro: a.erro ?? null,
      tentativas: a.tentativas ?? 0,
      servidorConfirmado: a.servidorConfirmado === true,
      enviadoEm: a.enviadoEm ?? null,
      atualizadoEm: Date.now()
    }));

    await banco.transaction("rw", tabela, async () => {
      await tabela
        .where("draft")
        .equals(draft)
        .delete();
      if (registros.length) await tabela.bulkPut(registros);
    });
  } catch (_e) {
    void _e;
  }
}

/** Lê os pendentes do rascunho (ignora registros velhos). */
export async function carregarAnexosPendentes(draft) {
  const banco = getDb();
  if (!banco || !draft) return [];
  try {
    const itens = await banco
      .table(TABELA)
      .where("draft")
      .equals(draft)
      .toArray();
    const limite = Date.now() - TTL_MS;
    return (itens || [])
      .filter(a => a && a.atualizadoEm >= limite && a.arquivo instanceof Blob)
      .map(a => ({
        ...a,
        id: a.localId != null ? a.localId : a.id
      }));
  } catch (_e) {
    return [];
  }
}

/** Remove ids específicos (ex.: após upload com sucesso). */
export async function removerAnexosPendentes(draft, ids) {
  const banco = getDb();
  if (!banco || !draft || !ids?.length) return;
  try {
    const chaves = ids.map(id => String(id));
    await banco
      .table(TABELA)
      .where("draft")
      .equals(draft)
      .and(item => chaves.includes(String(item.localId ?? item.id)))
      .delete();
  } catch (_e) {
    void _e;
  }
}

/** Limpa tudo do rascunho (ex.: limpar seleção, voltar à listagem). */
export async function limparAnexosPendentes(draft) {
  const banco = getDb();
  if (!banco || !draft) return;
  try {
    await banco
      .table(TABELA)
      .where("draft")
      .equals(draft)
      .delete();
  } catch (_e) {
    void _e;
  }
}

export async function carregarTiposOrigemAnexosServidor(draft) {
  const banco = getDb();
  if (!banco || !draft) return [];
  try {
    const tabela = banco.table(TABELA_ORIGEM);
    const itens = await tabela
      .where("draft")
      .equals(draft)
      .toArray();
    const limite = Date.now() - TTL_MS;
    const validos = (itens || []).filter(
      item => item && (!item.atualizadoEm || item.atualizadoEm >= limite)
    );
    const expirados = (itens || [])
      .filter(item => item && item.atualizadoEm && item.atualizadoEm < limite)
      .map(item => item.id);
    if (expirados.length) await tabela.bulkDelete(expirados);
    return validos;
  } catch (_e) {
    return [];
  }
}

export async function salvarTiposOrigemAnexosServidor(draft, anexos = []) {
  const banco = getDb();
  if (!banco || !draft) return;
  try {
    const tabela = banco.table(TABELA_ORIGEM);
    const existentes = await tabela
      .where("draft")
      .equals(draft)
      .toArray();
    const idsExistentes = new Set(
      (existentes || []).map(item => String(item.sqUpload))
    );
    const novos = (anexos || [])
      .filter(
        anexo =>
          anexo &&
          anexo.sqUpload != null &&
          anexo.tipoOcorrenciaOrigem != null &&
          !idsExistentes.has(String(anexo.sqUpload))
      )
      .map(anexo => ({
        id: `${String(draft)}:srv:${String(anexo.sqUpload)}`,
        draft,
        sqUpload: String(anexo.sqUpload),
        tipoOcorrenciaOrigem: String(anexo.tipoOcorrenciaOrigem),
        atualizadoEm: Date.now()
      }));
    if (novos.length) await tabela.bulkPut(novos);
  } catch (_e) {
    void _e;
  }
}

function anexoSemArquivo(anexo) {
  if (!anexo || typeof anexo !== "object") return anexo ?? null;
  const { arquivo, _raw, anexoAnterior, ...resto } = anexo;
  void arquivo;
  void _raw;
  return {
    ...resto,
    anexoAnterior: anexoAnterior ? anexoSemArquivo(anexoAnterior) : null
  };
}

export function anexoPorTipoResolvido(item) {
  const temBlob =
    typeof Blob !== "undefined" && item && item.arquivo instanceof Blob;
  if (!item || temBlob) return false;
  const dados = item.dados || {};
  return !!(
    dados.sqUpload != null ||
    dados.persistido === true ||
    dados.servidorConfirmado === true ||
    dados.statusUpload === "enviado" ||
    dados.status === "enviado"
  );
}

function chaveAnexoPorTipo(draft, tipo, anexo) {
  const id =
    anexo && anexo.id != null
      ? String(anexo.id)
      : `${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 8)}`;
  return `${String(draft)}:${String(tipo)}:${id}`;
}

export async function salvarAnexosPorTipo(draft, tipoOcorrencia, lista = []) {
  const banco = getDb();
  const tipo = tipoOcorrencia == null ? null : String(tipoOcorrencia);
  if (!banco || !draft || !tipo) return;
  try {
    const tabela = banco.table(TABELA_POR_TIPO);
    const registros = (lista || [])
      .filter(anexo => anexo && typeof anexo === "object")
      .map(anexo => ({
        id: chaveAnexoPorTipo(draft, tipo, anexo),
        draft,
        tipoOcorrencia: tipo,
        localId: anexo.id != null ? String(anexo.id) : null,
        dados: anexoSemArquivo(anexo),
        arquivo: anexo.arquivo instanceof Blob ? anexo.arquivo : null,
        anexoAnteriorArquivo:
          anexo.anexoAnterior && anexo.anexoAnterior.arquivo instanceof Blob
            ? anexo.anexoAnterior.arquivo
            : null,
        atualizadoEm: Date.now()
      }));

    await banco.transaction("rw", tabela, async () => {
      await tabela
        .where("draft")
        .equals(draft)
        .and(item => String(item.tipoOcorrencia) === tipo)
        .delete();
      if (registros.length) await tabela.bulkPut(registros);
    });
  } catch (_e) {
    void _e;
  }
}

export async function carregarAnexosPorTipo(draft, tipoOcorrencia) {
  const banco = getDb();
  const tipo = tipoOcorrencia == null ? null : String(tipoOcorrencia);
  if (!banco || !draft || !tipo) return [];
  try {
    const tabela = banco.table(TABELA_POR_TIPO);
    const itens = await tabela
      .where("draft")
      .equals(draft)
      .and(item => String(item.tipoOcorrencia) === tipo)
      .toArray();
    const limite = Date.now() - TTL_MS;
    const validos = (itens || []).filter(
      item => item && item.atualizadoEm >= limite
    );
    const expirados = (itens || [])
      .filter(item => item && item.atualizadoEm < limite)
      .map(item => item.id);
    if (expirados.length) await tabela.bulkDelete(expirados);
    return validos.map(item => ({
      ...(item.dados || {}),
      id: item.localId != null ? item.localId : item.id,
      arquivo: item.arquivo instanceof Blob ? item.arquivo : null,
      anexoAnteriorArquivo:
        item.anexoAnteriorArquivo instanceof Blob
          ? item.anexoAnteriorArquivo
          : null,
      tipoOcorrenciaOrigem:
        item.dados?.tipoOcorrenciaOrigem ?? item.tipoOcorrencia ?? null
    }));
  } catch (_e) {
    return [];
  }
}

export async function limparAnexosPorTipo(draft, tipoOcorrencia) {
  const banco = getDb();
  const tipo = tipoOcorrencia == null ? null : String(tipoOcorrencia);
  if (!banco || !draft || !tipo) return;
  try {
    await banco
      .table(TABELA_POR_TIPO)
      .where("draft")
      .equals(draft)
      .and(item => String(item.tipoOcorrencia) === tipo)
      .delete();
  } catch (_e) {
    void _e;
  }
}

export async function limparLixoAnexosPorTipo(draft) {
  const banco = getDb();
  if (!banco || !draft) return;
  try {
    const tabela = banco.table(TABELA_POR_TIPO);
    const itens = await tabela
      .where("draft")
      .equals(draft)
      .toArray();
    const limite = Date.now() - TTL_MS;
    const remover = (itens || [])
      .filter(item => {
        if (!item) return true;
        if (!item.atualizadoEm || item.atualizadoEm < limite) return true;
        return anexoPorTipoResolvido(item);
      })
      .map(item => item.id);
    if (remover.length) await tabela.bulkDelete(remover);
  } catch (_e) {
    void _e;
  }
}

export async function limparAnexosDoDraft(draft) {
  const banco = getDb();
  if (!banco || !draft) return;
  try {
    await banco.transaction(
      "rw",
      [TABELA, TABELA_ORIGEM, TABELA_POR_TIPO],
      async () => {
        await banco
          .table(TABELA)
          .where("draft")
          .equals(draft)
          .delete();
        await banco
          .table(TABELA_ORIGEM)
          .where("draft")
          .equals(draft)
          .delete();
        await banco
          .table(TABELA_POR_TIPO)
          .where("draft")
          .equals(draft)
          .delete();
      }
    );
  } catch (_e) {
    void _e;
  }
}
